import { useEffect, useMemo, useState } from 'react';
import { buildPivot, PIVOT_AGG_LABELS, type PivotAgg, type PivotResult } from '@/lib/pivot';
import { colLabel } from '@/lib/excelModel';

export interface PivotRange {
  r1: number;
  c1: number;
  r2: number;
  c2: number;
}

interface ExcelPivotDialogProps {
  open: boolean;
  sheetName: string;
  cells: Record<string, string>;
  selection: PivotRange | null;
  /** Zone réellement utilisée (repli quand aucune sélection). */
  usedRange: PivotRange | null;
  onInsert: (result: PivotResult, mode: 'active' | 'sheet', title: string) => void;
  onClose: () => void;
}

const AGGS: PivotAgg[] = ['sum', 'count', 'avg', 'min', 'max'];

function rangeLabel(range: PivotRange): string {
  return `${colLabel(range.c1)}${range.r1 + 1}:${colLabel(range.c2)}${range.r2 + 1}`;
}

/**
 * Fenêtre de configuration d'un tableau croisé dynamique : on choisit la plage,
 * le champ de ligne, le champ de colonne (facultatif), la valeur agrégée puis on
 * insère le résultat dans la feuille ou dans une nouvelle feuille.
 */
export default function ExcelPivotDialog({
  open,
  sheetName,
  cells,
  selection,
  usedRange,
  onInsert,
  onClose,
}: ExcelPivotDialogProps) {
  const range = selection || usedRange;
  const [rowField, setRowField] = useState(0);
  const [colField, setColField] = useState(-1);
  const [valueField, setValueField] = useState(1);
  const [agg, setAgg] = useState<PivotAgg>('sum');
  const [mode, setMode] = useState<'active' | 'sheet'>('active');
  const [title, setTitle] = useState('Tableau croisé dynamique');

  const width = range ? range.c2 - range.c1 + 1 : 0;

  const headers = useMemo(() => {
    if (!range) return [] as string[];
    return Array.from({ length: width }, (_, i) => {
      const raw = cells[`${range.r1},${range.c1 + i}`] || '';
      return raw.trim() || `Colonne ${colLabel(range.c1 + i)}`;
    });
  }, [range, cells, width]);

  useEffect(() => {
    if (!open) return;
    setRowField(0);
    setColField(width > 2 ? -1 : -1);
    setValueField(width > 1 ? 1 : 0);
    setAgg('sum');
    setMode('active');
    setTitle('Tableau croisé dynamique');
  }, [open, width]);

  const result = useMemo(() => {
    if (!open || !range || width < 1) return null;
    try {
      return buildPivot(cells, {
        r1: range.r1,
        c1: range.c1,
        r2: range.r2,
        c2: range.c2,
        rowField: Math.min(rowField, width - 1),
        colField: colField >= width ? -1 : colField,
        valueField: Math.min(valueField, width - 1),
        agg,
      });
    } catch {
      return null;
    }
  }, [open, range, cells, rowField, colField, valueField, agg, width]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative w-full max-w-3xl bg-background-50 rounded-xl border border-background-200/70 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 h-14 border-b border-background-200/70 flex-shrink-0">
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-layout-grid-line text-primary-500"></i>
              Tableau croisé dynamique
            </h3>
            <p className="text-[11px] text-foreground-500 truncate">
              Feuille « {sheetName} »{range ? ` · plage ${rangeLabel(range)}` : ' · aucune plage'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {!range ? (
          <div className="p-8 text-center">
            <i className="ri-square-line text-3xl text-foreground-300"></i>
            <p className="text-sm text-foreground-600 mt-2">
              Sélectionnez d’abord une plage de cellules (avec sa ligne d’en-tête).
            </p>
          </div>
        ) : (
          <>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-foreground-500 mb-1.5">
                    Champ de ligne
                  </label>
                  <select
                    value={rowField}
                    onChange={(e) => setRowField(Number(e.target.value))}
                    className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                  >
                    {headers.map((h, i) => (
                      <option key={`row-${h}-${i}`} value={i}>{h}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-foreground-500 mb-1.5">
                    Champ de colonne
                  </label>
                  <select
                    value={colField}
                    onChange={(e) => setColField(Number(e.target.value))}
                    className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                  >
                    <option value={-1}>Aucun</option>
                    {headers.map((h, i) => (
                      <option key={`col-${h}-${i}`} value={i} disabled={i === rowField}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-foreground-500 mb-1.5">
                    Valeur à calculer
                  </label>
                  <select
                    value={valueField}
                    onChange={(e) => setValueField(Number(e.target.value))}
                    className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                  >
                    {headers.map((h, i) => (
                      <option key={`val-${h}-${i}`} value={i}>{h}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-foreground-500 mb-1.5">
                    Opération
                  </label>
                  <select
                    value={agg}
                    onChange={(e) => setAgg(e.target.value as PivotAgg)}
                    className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                  >
                    {AGGS.map((a) => (
                      <option key={a} value={a}>{PIVOT_AGG_LABELS[a]}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-foreground-500 mb-1.5">
                  Titre du tableau
                </label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={80}
                  className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500">Aperçu</p>
                  {result && (
                    <span className="text-[11px] text-foreground-400">
                      {result.rows.length} ligne{result.rows.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="border border-background-200/70 rounded-lg overflow-auto max-h-64 bg-white">
                  {result ? (
                    <table className="w-full text-[11px] border-collapse">
                      <thead>
                        <tr className="bg-background-100">
                          {result.headers.map((h, i) => (
                            <th
                              key={`ph-${h}-${i}`}
                              className="px-2 py-1.5 text-left font-semibold text-foreground-800 border-b border-background-200/70 whitespace-nowrap"
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.rows.map((row, ri) => (
                          <tr key={`pr-${ri}`} className={ri % 2 ? 'bg-background-50/60' : ''}>
                            {row.map((cell, ci) => (
                              <td
                                key={`pc-${ri}-${ci}`}
                                className={`px-2 py-1 border-b border-background-100 ${
                                  ci === 0 ? 'font-medium text-foreground-800' : 'text-foreground-700 text-right'
                                }`}
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                        <tr className="bg-secondary-50">
                          {result.totals.map((cell, ci) => (
                            <td
                              key={`pt-${ci}`}
                              className={`px-2 py-1.5 font-semibold text-secondary-900 ${
                                ci === 0 ? '' : 'text-right'
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  ) : (
                    <p className="p-4 text-xs text-foreground-400 text-center">Aucune donnée exploitable.</p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-500 mb-1.5">
                  Destination
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setMode('active')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                      mode === 'active'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                    }`}
                  >
                    <i className="ri-table-line"></i>
                    Dans la feuille (sous les données)
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('sheet')}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${
                      mode === 'sheet'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                    }`}
                  >
                    <i className="ri-add-box-line"></i>
                    Nouvelle feuille
                  </button>
                </div>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-background-200/70 flex items-center justify-end gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={!result || result.rows.length === 0}
                onClick={() => {
                  if (result) onInsert(result, mode, title.trim() || 'Tableau croisé dynamique');
                  onClose();
                }}
                className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-check-line"></i>
                Insérer le tableau
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}